import React from 'react';

export interface FlexProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Flex direction
   */
  direction?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  /**
   * Justify content (main axis alignment)
   */
  justify?: 'start' | 'end' | 'center' | 'between' | 'around' | 'evenly';
  /**
   * Align items (cross axis alignment)
   */
  align?: 'start' | 'end' | 'center' | 'baseline' | 'stretch';
  /**
   * Gap between items
   */
  gap?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Flex wrap
   */
  wrap?: boolean;
  /**
   * Flex grow/shrink/basis shorthand (e.g., 1, '0 0 auto', '1 1 200px')
   */
  flex?: string | number;
  /**
   * Children elements
   */
  children: React.ReactNode;
  /**
   * Custom CSS classes
   */
  className?: string;
  /**
   * Inline styles
   */
  style?: React.CSSProperties;
  /**
   * HTML element to render as
   */
  as?: 'div' | 'section' | 'nav' | 'header' | 'footer' | 'main' | 'aside';
}

export const Flex: React.FC<FlexProps> = ({
  direction = 'row',
  justify = 'start',
  align = 'stretch',
  gap = 'none',
  wrap = false,
  flex,
  children,
  className = '',
  style,
  as = 'div',
  ...rest
}) => {
  // Base class
  const baseClass = 'hi-ui-flex';

  // Direction class
  const directionClass = `hi-ui-flex-${direction}`;

  // Justify class
  const justifyClass = justify !== 'start' ? `hi-ui-flex-justify-${justify}` : '';

  // Align class
  const alignClass = align !== 'stretch' ? `hi-ui-flex-align-${align}` : '';

  // Gap class
  const gapClass = gap !== 'none' ? `hi-ui-flex-gap-${gap}` : '';

  // Wrap class
  const wrapClass = wrap ? 'hi-ui-flex-wrap' : '';

  const allClasses =
    `${baseClass} ${directionClass} ${justifyClass} ${alignClass} ${gapClass} ${wrapClass} ${className}`.trim();

  // Merge flex prop into style
  const mergedStyle = flex ? { ...style, flex } : style;

  const props = {
    ...rest,
    className: allClasses,
    style: mergedStyle,
  };

  return React.createElement(as, props, children);
};
